import * as React from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export type ContactPickerRow = {
  id: string;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
};

export function ContactSelectCombobox({
  id,
  value,
  onValueChange,
  onBlur,
  contacts,
  disabled = false,
  loading = false,
  allowNew = true,
  allowNone = true,
  noneLabel,
  newContactLabel,
  searchPlaceholder,
  emptyLabel,
  loadingLabel,
  triggerClassName,
  "aria-invalid": ariaInvalid,
  /** Modal Dialog/Sheet belseje — Popover ide portál, hogy a lista görgethető legyen. */
  portalContainer,
}: {
  id?: string;
  value: string;
  onValueChange: (next: string) => void;
  onBlur?: () => void;
  contacts: ContactPickerRow[];
  disabled?: boolean;
  loading?: boolean;
  allowNew?: boolean;
  allowNone?: boolean;
  noneLabel: string;
  newContactLabel: string;
  searchPlaceholder: string;
  emptyLabel: string;
  loadingLabel?: string;
  triggerClassName?: string;
  "aria-invalid"?: boolean;
  portalContainer?: HTMLElement | null;
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => {
      const name = (c.name || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      const phone = (c.phone || "").toLowerCase().replace(/\s/g, "");
      const qPhone = q.replace(/\s/g, "");
      return name.includes(q) || email.includes(q) || phone.includes(qPhone);
    });
  }, [contacts, search]);

  const selectedLabel = React.useMemo(() => {
    if (loading && loadingLabel) return loadingLabel;
    if (loading) return "…";
    if (value === "") return noneLabel;
    if (value === "new") return newContactLabel;
    const c = contacts.find((x) => x.id === value);
    return c ? String(c.name || c.email || c.id) : noneLabel;
  }, [value, contacts, noneLabel, newContactLabel, loading, loadingLabel]);

  const clearSearchAndBlur = React.useCallback(() => {
    setSearch("");
    onBlur?.();
  }, [onBlur]);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) clearSearchAndBlur();
  };

  const pick = (next: string) => {
    onValueChange(next);
    setOpen(false);
    clearSearchAndBlur();
  };

  const noContactMatches = search.trim().length > 0 && filtered.length === 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          disabled={disabled || loading}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            triggerClassName,
          )}
        >
          <span className="truncate text-left">{selectedLabel}</span>
          {loading ? (
            <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        container={portalContainer ?? undefined}
        className="z-[100] flex max-h-[min(320px,calc(100dvh-10rem))] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden p-0"
        align="start"
        side="bottom"
        sideOffset={4}
        avoidCollisions={false}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false} className="min-h-0 max-h-full flex-1 flex-col overflow-hidden">
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
            autoFocus={false}
          />
          <CommandList className="min-h-0 max-h-[min(292px,calc(100dvh-12rem))] flex-1 overflow-y-auto overscroll-y-contain touch-pan-y">
            <CommandGroup>
              {allowNone ? (
                <CommandItem
                  value="__none__"
                  onSelect={() => {
                    pick("");
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === "" ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{noneLabel}</span>
                </CommandItem>
              ) : null}
              {allowNew ? (
                <CommandItem
                  value="__new_contact__"
                  onSelect={() => {
                    pick("new");
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === "new" ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{newContactLabel}</span>
                </CommandItem>
              ) : null}
            </CommandGroup>
            {filtered.length > 0 ? (
              <CommandGroup>
                {filtered.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`id:${c.id}`}
                    onSelect={() => {
                      pick(c.id);
                    }}
                  >
                    <Check
                      className={cn("mr-2 h-4 w-4 shrink-0", value === c.id ? "opacity-100" : "opacity-0")}
                    />
                    <span className="truncate">{c.name || c.email || c.id}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
            {noContactMatches ? (
              <div className="border-t px-2 py-3 text-center text-sm text-muted-foreground">{emptyLabel}</div>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
